import { ApiClient } from 'mycheck-core';
import {
  HttpCheckoutApiRequest,
  HttpCheckoutApiResponse,
  ICheckoutApi,
} from './types/CheckoutTypes';

const CheckoutUrls = {
  cartCreate: '/restaurants/api/v3/cart',
  cartUpdate: (id: string) => `/restaurants/api/v3/cart/${id}`,
  cartCheckout: (id: string) => `/restaurants/api/v3/cart/${id}/checkout`,
  cartDelete: (id: string) => `/restaurants/api/v3/cart/${id}/checkout`,
  roomValidation: (model: HttpCheckoutApiRequest['roomValidation']) =>
    `/restaurants/api/v3/${model.bid}/roomChargeValidity?lastName=${model.lastName}&roomNumber=${model.roomNumber}`,
  roomValidity: (model: HttpCheckoutApiRequest['roomValidation']) =>
    `/restaurants/api/v3/${model.bid}/roomValidity?lastName=${model.lastName}&roomNumber=${model.roomNumber}`,
  confirm3DSecure: (orderId: string, paymentId: number) =>
    `/restaurants/api/v3/order/${orderId}/payment/${paymentId}/transaction`,
  couponVerify: `rewards/api/v1/rewards/verify`,
  cancelTransaction: (orderId: string, paymentId: number) =>
    `/restaurants/api/v3/order/${orderId}/payment/${paymentId}/cancel`,
  createAstralWalletTransaction: (
    model: HttpCheckoutApiRequest['createAstralWalletTransaction'],
  ) => `/restaurants/api/v3/cart/${model.cart_id}/pay-and-checkout`,
  getAstralWalletIframeData: (
    model: HttpCheckoutApiRequest['getAstralWalletIframeData'],
  ) => `/payment-dispatcher/api/payments/iframe/${model.paymentId}`,
  astralWalletTransactionStatus: (
    model: HttpCheckoutApiRequest['astralWalletTransactionStatus'],
  ) => `/payment-dispatcher/api/payments/status/${model.uuid}`,
  refundAstralWalletPayment: (
    model: HttpCheckoutApiRequest['refundAstralWalletPayment'],
  ) => `/payment-dispatcher/api/payments/refund/${model.uuid}`,
  getOrderConfirmationDetails: (
    model: HttpCheckoutApiRequest['getOrderConfirmationDetails'],
  ) => `/restaurants/api/v3/order/${model.orderId}/confirmation`,
  retryAstralWalletCheckout: (
    model: HttpCheckoutApiRequest['retryAstralWalletCheckout'],
  ) => `/payment-dispatcher/api/payments/${model.paymentId}/retry-checkout`,
  orderGuestStatus: (id: string) =>
    `/restaurants/api/v3/order-guest-status/${id}`,
  notificationOpt: (id: string) =>
    `/restaurants/api/v3/order-guest-status/${id}/notifications/opt`,
};

export const HTTP_CHECKOUT_API = 'HTTP_CHECKOUT_API';

export class HttpCheckoutApi implements ICheckoutApi {
  postCreateCart = async (
    model: HttpCheckoutApiRequest['updateCart'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.post<
      HttpCheckoutApiRequest['updateCart'],
      HttpCheckoutApiResponse['iCreateNewCart']
    >(CheckoutUrls.cartCreate, model, {
      headers: { Authorization: accessToken },
    });
    return response.data;
  };

  putUpdateCart = async (
    model: HttpCheckoutApiRequest['updateCart'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.put<
      HttpCheckoutApiRequest['updateCart'],
      HttpCheckoutApiResponse['iCreateNewCart']
    >(CheckoutUrls.cartUpdate(model.id), model, {
      headers: { Authorization: accessToken },
    });
    return response.data;
  };

  postCheckoutCart = async (
    model: HttpCheckoutApiRequest['checkoutCart'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.post<
      HttpCheckoutApiRequest['checkoutCart'],
      HttpCheckoutApiResponse['iCheckoutCart']
    >(CheckoutUrls.cartCheckout(model.id), model, {
      headers: { Authorization: accessToken },
    });

    return response.data;
  };

  deleteCheckoutCart = async (cartId: string, accessToken: string) => {
    await ApiClient.instance.delete(CheckoutUrls.cartDelete(cartId), {
      headers: { Authorization: accessToken },
    });
  };

  roomValidation = async (
    model: HttpCheckoutApiRequest['roomValidation'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.post<
      HttpCheckoutApiRequest['roomValidation'],
      HttpCheckoutApiResponse['iRoomValidation']
    >(CheckoutUrls.roomValidation(model), null, {
      headers: { Authorization: accessToken },
    });
    return response.data;
  };

  roomValidity = async (
    model: HttpCheckoutApiRequest['roomValidation'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.post<
      HttpCheckoutApiRequest['roomValidation'],
      HttpCheckoutApiResponse['iRoomValidation']
    >(CheckoutUrls.roomValidity(model), null, {
      headers: { Authorization: accessToken },
    });
    return response.data;
  };

  confirm3DSecure = async (
    orderId: string,
    paymentId: number,
    transactionId: string | number,
  ) => {
    await ApiClient.instance.put(
      CheckoutUrls.confirm3DSecure(orderId, paymentId),
      { transaction_id: transactionId },
    );
  };

  cancelTransaction = async (orderId: string, paymentId: number) => {
    await ApiClient.instance.post(
      CheckoutUrls.cancelTransaction(orderId, paymentId),
    );
  };

  createAstralWalletTransaction = async (
    model: HttpCheckoutApiRequest['createAstralWalletTransaction'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.post<
      HttpCheckoutApiRequest['createAstralWalletTransaction'],
      HttpCheckoutApiResponse['iCreateAstralWalletTransaction']
    >(CheckoutUrls.createAstralWalletTransaction(model), model, {
      headers: {
        Authorization: accessToken,
      },
    });

    return response.data;
  };

  getAstralWalletIframeData = async (
    model: HttpCheckoutApiRequest['getAstralWalletIframeData'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.get<
      HttpCheckoutApiResponse['iGetAstralWalletIframeData']
    >(CheckoutUrls.getAstralWalletIframeData(model), null, {
      headers: {
        Authorization: accessToken,
      },
    });

    return response.data;
  };

  astralWalletTransactionStatus = async (
    model: HttpCheckoutApiRequest['astralWalletTransactionStatus'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.get<
      HttpCheckoutApiResponse['iAstralWalletTransactionStatus']
    >(CheckoutUrls.astralWalletTransactionStatus({ uuid: model.uuid }), null, {
      headers: { Authorization: accessToken },
    });

    return response.data;
  };

  getOrderGuestStatus = async (id: string, accessToken: string) => {
    const response = await ApiClient.instance.get<
      HttpCheckoutApiResponse['iOrderGuestStatus']
    >(CheckoutUrls.orderGuestStatus(id), null, {
      headers: { Authorization: accessToken },
    });

    return response.data;
  };

  setGuestOrderStatusNotificationOpt = async (
    model: HttpCheckoutApiRequest['setGuestOrderStatusNotificationOpt'],
    accessToken: string,
  ) => {
    await ApiClient.instance.put<
      HttpCheckoutApiRequest['setGuestOrderStatusNotificationOpt']
    >(CheckoutUrls.notificationOpt(model.id), model, {
      headers: { Authorization: accessToken },
    });
  };

  getOrderConfirmationDetails = async (
    model: HttpCheckoutApiRequest['getOrderConfirmationDetails'],
    accessToken: string,
  ) => {
    const response = await ApiClient.instance.get<
      HttpCheckoutApiResponse['iGetOrderDetailsConfirmation']
    >(CheckoutUrls.getOrderConfirmationDetails(model), null, {
      headers: {
        Authorization: accessToken,
      },
    });

    return response.data;
  };

  refundAstralWalletPayment = async (
    model: HttpCheckoutApiRequest['refundAstralWalletPayment'],
    accessToken: string,
  ) => {
    await ApiClient.instance.post<
      HttpCheckoutApiRequest['refundAstralWalletPayment'],
      unknown
    >(CheckoutUrls.refundAstralWalletPayment(model), model, {
      headers: { Authorization: accessToken },
    });
  };

  retryAstralWalletCheckout = async (
    model: HttpCheckoutApiRequest['retryAstralWalletCheckout'],
    accessToken: string,
  ) => {
    await ApiClient.instance.post<
      HttpCheckoutApiResponse['iGetOrderDetailsConfirmation']
    >(CheckoutUrls.retryAstralWalletCheckout(model), null, {
      headers: {
        Authorization: accessToken,
      },
    });
  };
}
